/**
 * Public Homepage: "Tonton Preview Kelas" section.
 *
 * Displays admin-managed preview videos. Featured video is shown large with
 * a Vimeo player + CTA box. Remaining videos appear as clickable cards.
 * Clicking a card promotes it to the main player (does NOT navigate).
 *
 * Backend contract: GET /api/preview-videos returns published previews sorted
 * with featured first. When list is empty, the entire section renders NOTHING
 * (no broken containers, no empty CTA boxes — as per spec).
 */
import { useEffect, useMemo, useState } from "react";
import { Play, MessageCircle, ExternalLink, CreditCard, Clock, User as UserIcon } from "lucide-react";
import api, { fileUrl } from "@/lib/api";

function CtaButtons({ video }) {
    const hasPrimary = video.primary_cta_enabled && video.primary_cta_url;
    const hasOnPay = video.onpay_cta_enabled && video.onpay_url;
    const hasWa = video.whatsapp_cta_enabled && (video.whatsapp_url || video.whatsapp_number);

    if (!hasPrimary && !hasOnPay && !hasWa) return null;

    const waUrl = video.whatsapp_url || (video.whatsapp_number
        ? `https://wa.me/${(video.whatsapp_number || "").replace(/\D/g, "")}` +
          (video.whatsapp_message ? `?text=${encodeURIComponent(video.whatsapp_message)}` : "")
        : "");

    return (
        <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            {hasPrimary && (
                <a href={video.primary_cta_url} target="_blank" rel="noopener noreferrer"
                    className="btn-premium inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm relative overflow-hidden"
                    data-testid={`preview-cta-primary-${video.id}`}>
                    <CreditCard className="h-4 w-4 relative z-10" />
                    <span className="relative z-10">{video.primary_cta_label || "Daftar Sekarang"}</span>
                </a>
            )}
            {hasOnPay && (
                <a href={video.onpay_url} target="_blank" rel="noopener noreferrer"
                    className="btn-outline-premium inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm"
                    data-testid={`preview-cta-onpay-${video.id}`}>
                    <ExternalLink className="h-4 w-4" />
                    <span>{video.onpay_cta_label || "Daftar Melalui OnPay"}</span>
                </a>
            )}
            {hasWa && (
                <a href={waUrl} target="_blank" rel="noopener noreferrer"
                    className="btn-outline-premium inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm border-green-500/40 hover:!border-green-500/70 hover:!bg-green-500/10 hover:!text-green-500"
                    data-testid={`preview-cta-wa-${video.id}`}>
                    <MessageCircle className="h-4 w-4" />
                    <span>{video.whatsapp_cta_label || "Tanya Admin"}</span>
                </a>
            )}
        </div>
    );
}

function VideoMeta({ video, className = "" }) {
    const bits = [video.course_name, video.instructor_name, video.duration].filter(Boolean);
    if (!bits.length) return null;
    return (
        <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground ${className}`}>
            {video.course_name && <span className="inline-flex items-center gap-1"><CreditCard className="h-3 w-3" />{video.course_name}</span>}
            {video.instructor_name && <span className="inline-flex items-center gap-1"><UserIcon className="h-3 w-3" />{video.instructor_name}</span>}
            {video.duration && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{video.duration}</span>}
        </div>
    );
}

export function HomepagePreviewSection() {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [playing, setPlaying] = useState(false);

    useEffect(() => {
        api.get("/preview-videos")
            .then((r) => {
                const list = (r.data || []).filter((v) => v.show_in_preview_section !== false);
                setVideos(list);
                setSelectedId(list[0]?.id || null);
            })
            .catch(() => setVideos([]))
            .finally(() => setLoading(false));
    }, []);

    const active = useMemo(() => videos.find((v) => v.id === selectedId) || videos[0], [videos, selectedId]);
    const others = useMemo(() => videos.filter((v) => v.id !== active?.id), [videos, active]);

    // Per spec: hide the entire section when no published preview videos exist.
    if (loading) return null;
    if (!videos.length || !active) return null;

    return (
        <section className="relative py-20 sm:py-24" data-testid="preview-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                {/* Header */}
                <div className="text-center mb-10 sm:mb-14 max-w-2xl mx-auto">
                    <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">Preview Kelas</p>
                    <h2 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight mt-3">
                        Tonton Preview Kelas
                    </h2>
                    <p className="text-sm sm:text-base text-muted-foreground mt-4 leading-relaxed">
                        Lihat kandungan, gaya penerangan dan pengalaman pembelajaran sebelum membuat keputusan untuk mendaftar.
                    </p>
                </div>

                <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
                    {/* MAIN player */}
                    <div className="lg:col-span-3 space-y-5">
                        <div className="glass-card overflow-hidden">
                            <div className="relative aspect-video bg-black">
                                {playing ? (
                                    <iframe
                                        key={active.id}
                                        src={`${active.vimeo_url}${active.vimeo_url.includes("?") ? "&" : "?"}autoplay=1`}
                                        className="absolute inset-0 w-full h-full"
                                        allow="autoplay; fullscreen; picture-in-picture"
                                        allowFullScreen
                                        title={active.title}
                                        data-testid="preview-iframe"
                                        onError={() => console.warn("Preview video failed to load")}
                                    />
                                ) : (
                                    <button
                                        onClick={() => setPlaying(true)}
                                        className="absolute inset-0 group cursor-pointer"
                                        data-testid="preview-play-btn"
                                        aria-label="Play preview video"
                                    >
                                        {active.thumbnail_url ? (
                                            <img src={fileUrl(active.thumbnail_url)} alt={active.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                                        ) : (
                                            <img src={`https://vumbnail.com/${active.vimeo_video_id}.jpg`} alt={active.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="icon-tile icon-tile-teal h-16 w-16 rounded-full transition-transform duration-300 group-hover:scale-110 shadow-2xl">
                                                <Play className="h-6 w-6 ml-1" fill="currentColor" strokeWidth={0} />
                                            </span>
                                        </div>
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="space-y-3">
                            <h3 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight">{active.title}</h3>
                            {active.description && (
                                <p className="text-sm text-muted-foreground leading-relaxed">{active.description}</p>
                            )}
                            <VideoMeta video={active} />
                            <div className="pt-2">
                                <CtaButtons video={active} />
                            </div>
                        </div>
                    </div>

                    {/* SIDE cards */}
                    {others.length > 0 && (
                        <div className="lg:col-span-2 space-y-3 lg:max-h-[560px] lg:overflow-y-auto pr-1">
                            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">More previews</p>
                            {others.map((v) => (
                                <button
                                    key={v.id}
                                    onClick={() => { setSelectedId(v.id); setPlaying(false); }}
                                    className="glass-card glass-card-hover w-full flex items-center gap-3 p-3 text-left"
                                    data-testid={`preview-card-${v.id}`}
                                >
                                    <div className="relative w-28 aspect-video rounded-md overflow-hidden bg-muted/50 flex-shrink-0 border border-border">
                                        {v.thumbnail_url ? (
                                            <img src={fileUrl(v.thumbnail_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                        ) : (
                                            <img src={`https://vumbnail.com/${v.vimeo_video_id}.jpg`} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                        )}
                                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                            <Play className="h-5 w-5 text-white" fill="currentColor" strokeWidth={0} />
                                        </div>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold truncate">{v.title}</p>
                                        {v.course_name && <p className="text-xs text-muted-foreground truncate">{v.course_name}</p>}
                                        {v.duration && (
                                            <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1">
                                                <Clock className="h-3 w-3" />{v.duration}
                                            </p>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
